<template>
  <div class="reports-filters flex">
    <div class="relative flex flex-wrap mb-3">
      <BaseButton
        ref="adjust-filters-btn"
        :class="[
          'flex justify-center items-center rounded-full text-xs px-4 py-2 mt-2 mr-2',
          {
            'bg-white shadow-active text-deep-cerulean hover:bg-alabaster': showFiltersPanel,
            'bg-deep-cerulean text-white hover:bg-venice-blue': !showFiltersPanel
          }
        ]"
        @click="toggleFiltersPanel"
      >
        <BaseIcon name="sliders-h" height="20" width="20" />
        <span class="pl-2">Adjust Filters</span>
      </BaseButton>
      <BaseTag
        tagStyle="primary"
        class="flex items-center font-semibold p-2 mr-2 mt-2 rounded text-xs"
        v-if="showDateRangeTag"
      >
        <BaseIcon name="calendar" height="12" width="12" />
        <span class="pl-1">{{ dateTagDescription }}</span>
      </BaseTag>
      <BaseTag
        :key="tag.id"
        v-for="tag in currentTags"
        tagStyle="primary"
        class="font-semibold p-2 mr-2 mt-2 rounded text-xs"
      >
        {{ tag.categoryName }}: {{ tag.name }}
      </BaseTag>
      <BaseTransition type="fade">
        <FiltersPanel
          class="absolute bg-white mt-3 z-10"
          :pinFilters="pinFilters"
          @apply="applyFilters"
          @close="toggleFiltersPanel"
          @toggle-pin-filters="togglePinFilters"
          v-click-outside="filtersClickOutsideOptions"
          v-if="showFiltersPanel"
        >
          <Loading
            :class="{
              'mt-4': isLoadingFilters || errorLoadingFilters
            }"
            :isLoading="isLoadingFilters"
            :hasError="errorLoadingFilters"
          >
            <template #success>
              <List class="pt-2 px-2">
                <DateFilterCategory
                  class="mb-2"
                  id="date-range"
                  :open="Boolean(categoriesOpen['date-range'])"
                  @toggle="toggleFilterCategory"
                />
                <FilterCategory
                  class="mb-2"
                  :appliedFilters="appliedFilters[category.id]"
                  :filters="category.choices"
                  :id="category.id"
                  :name="category.attribute"
                  :open="Boolean(categoriesOpen[category.id])"
                  :type="category.type"
                  @change="updateCategoryFilters"
                  @clear="clearCategoryFilters"
                  @toggle="toggleFilterCategory"
                  :key="category.id"
                  v-for="category in categories"
                />
              </List>
            </template>

            <template #error>
              <div class="mt-4">
                <LoadingError
                  icon="user-error"
                  iconHeight="30"
                  iconWidth="30"
                  :withRetry="true"
                  message="We're having trouble loading the filters."
                  subMessage="Please try again."
                  @retry="getFilters"
                />
              </div>
            </template>
          </Loading>
        </FiltersPanel>
      </BaseTransition>
    </div>
    <div>
      <BaseButton
        class="flex items-center text-xs font-semibold text-abbey px-4 py-2 mt-2"
        @click="onClear"
        v-if="numberOfCurrentTags"
      >
        <span>Clear</span>
        <div class="flex justify-center items-center relative pl-1">
          <BaseIcon
            class="text-deep-cerulean"
            name="circle"
            height="22"
            width="22"
          />
          <span class="absolute text-white">{{ numberOfCurrentTags }}</span>
        </div>
      </BaseButton>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  clickOutside,
  List,
  Loading,
  LoadingError
} from '@listen360/alexandria'
import FilterCategory from '@/components/FiltersPanel/FilterCategory'
import DateFilterCategory from './DateFilterCategory'
import FiltersPanel from '@/components/FiltersPanel'
import format from 'date-fns/format'

export default {
  name: 'ReportsFilters',
  components: {
    DateFilterCategory,
    FilterCategory,
    FiltersPanel,
    List,
    Loading,
    LoadingError
  },
  directives: {
    clickOutside
  },
  data() {
    return {
      currentDateRange: [],
      currentTags: [],
      filtersClickOutsideOptions: {
        exclude: ['adjust-filters-btn'],
        handler: this.toggleFiltersPanel
      },
      pinFilters: false,
      showFiltersPanel: false
    }
  },
  mounted() {
    this.getFilters()
  },
  computed: {
    ...mapGetters({
      categories: 'reports/filterCategories',
      errorLoadingFilters: 'reports/errorLoadingFilters',
      isLoadingFilters: 'reports/isLoadingFilters',
      categoriesOpen: 'reports/filterCategoriesOpen',
      appliedFilters: 'reports/appliedFilters',
      appliedDateRange: 'reports/appliedDateRange',
      tags: 'reports/tags'
    }),
    dateTagDescription() {
      return this.showDateRangeTag
        ? `${format(this.currentDateRange[0], 'MM/dd/yyyy')}-${format(
            this.currentDateRange[1],
            'MM/dd/yyyy'
          )}`
        : ''
    },
    showDateRangeTag() {
      return Boolean(this.currentDateRange[0] && this.currentDateRange[1])
    },
    canApplyDateRange() {
      return Boolean(this.appliedDateRange[0] && this.appliedDateRange[1])
    },
    numberOfCurrentTags() {
      return this.showDateRangeTag
        ? this.currentTags.length + 1
        : this.currentTags.length
    }
  },
  methods: {
    ...mapActions({
      getFilters: 'reports/getFilters',
      toggleFilterCategory: 'reports/toggleFilterCategory',
      clearOpenFilterCategories: 'reports/clearOpenFilterCategories',
      updateCategoryFilters: 'reports/updateCategoryFilters',
      clearCategoryFilters: 'reports/clearCategoryFilters',
      clearAllCategoryFilters: 'reports/clearAllCategoryFilters',
      clearAppliedDateRange: 'reports/clearAppliedDateRange'
    }),
    toggleFiltersPanel() {
      this.showFiltersPanel = !this.showFiltersPanel

      if (!this.showFiltersPanel) {
        this.clearOpenFilterCategories()
      }
    },
    togglePinFilters() {
      this.pinFilters = !this.pinFilters
    },
    applyFilters() {
      this.currentTags = this.tags

      if (this.canApplyDateRange) {
        this.currentDateRange = this.appliedDateRange
      }

      this.toggleFiltersPanel()
    },
    onClear() {
      this.clearAllCategoryFilters()
      this.clearAppliedDateRange()

      this.currentTags = []
      this.currentDateRange = []
    }
  }
}
</script>

<style>
.reports-filters .filters-panel {
  height: calc(100vh - 255px);
  max-height: 600px;
  width: 450px;
  top: 44px;
}
</style>
